/**
 * Business Registration Page
 * Public page for new business registration
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import portalService from '../../services/portal.service';
import type { BusinessRegistrationData, BusinessRole } from '../../types/portal.types';

const BUSINESS_TYPES: { value: BusinessRole; label: string; description: string }[] = [
  { value: 'PFLOJ', label: 'Loja/Comercio', description: 'Estabelecimento comercial que vende produtos' },
  { value: 'PFEMP', label: 'Empresa', description: 'Empresa que presta servicos' },
  { value: 'PFGOV', label: 'Governo', description: 'Entidade governamental' },
  { value: 'PFONG', label: 'ONG/Associacao', description: 'Organizacao sem fins lucrativos' },
  { value: 'PFEVT', label: 'Promotor de Eventos', description: 'Organizador de eventos' },
  { value: 'PFPRO', label: 'Profissional Liberal', description: 'Profissional independente' },
];

const BusinessRegister: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [registrationId, setRegistrationId] = useState<string | null>(null);

  const [formData, setFormData] = useState<BusinessRegistrationData>({
    tipoPerfil: 'PFLOJ',
    nomeNegocio: '',
    email: '',
    username: '',
    password: '',
  });

  const [confirmPassword, setConfirmPassword] = useState('');

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const validateStep1 = () => {
    return formData.tipoPerfil && formData.nomeNegocio && formData.email;
  };

  const validateStep2 = () => {
    return formData.username && formData.password && formData.password === confirmPassword && formData.password.length >= 6;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== confirmPassword) {
      setError('As passwords nao coincidem');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await portalService.registerBusiness(formData);
      setRegistrationId(result.registrationId || result.id);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Erro ao registar negócio');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Registo Submetido!</h1>
          <p className="text-gray-600 mb-6">
            O seu pedido de registo foi submetido com sucesso. A nossa equipa irá analisar e aprovar o seu negócio em breve.
          </p>
          {registrationId && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">Numero de referencia:</p>
              <p className="font-mono font-medium text-gray-900">{registrationId}</p>
            </div>
          )}
          <p className="text-sm text-gray-500 mb-6">
            Enviamos um email de confirmação para <strong>{formData.email}</strong> com mais informações.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 transition-colors"
          >
            Ir para o Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Registo de Negocio</h1>
          <p className="text-gray-600">Junte-se ao Pagali e comece a receber pagamentos digitais</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-pagali-primary text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-20 h-1 ${step > s ? 'bg-pagali-primary' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Business Info */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações do Negócio</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Negocio *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {BUSINESS_TYPES.map((type) => (
                      <label
                        key={type.value}
                        className={`flex items-start p-4 border rounded-lg cursor-pointer transition-all ${
                          formData.tipoPerfil === type.value
                            ? 'border-pagali-primary bg-pagali-primary/5 ring-2 ring-pagali-primary'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tipoPerfil"
                          value={type.value}
                          checked={formData.tipoPerfil === type.value}
                          onChange={(e) => setFormData({ ...formData, tipoPerfil: e.target.value as BusinessRole })}
                          className="sr-only"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{type.label}</p>
                          <p className="text-sm text-gray-500">{type.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Negocio *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nomeNegocio}
                    onChange={(e) => setFormData({ ...formData, nomeNegocio: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    placeholder="Nome da sua empresa ou negocio"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                      placeholder="email@empresa.cv"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={formData.telefone || ''}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                      placeholder="+238 XXX XXXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NIF (Número de Identificação Fiscal)
                  </label>
                  <input
                    type="text"
                    value={formData.nif || ''}
                    onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    placeholder="Número de contribuinte"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Account Info */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Conta de Acesso</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo do Responsavel
                  </label>
                  <input
                    type="text"
                    value={formData.nomeCompleto || ''}
                    onChange={(e) => setFormData({ ...formData, nomeCompleto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    placeholder="Nome do responsavel pelo negocio"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    placeholder="Nome de utilizador"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                      placeholder="Minimo 6 caracteres"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar Password *
                    </label>
                    <input
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        confirmPassword && confirmPassword !== formData.password
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-pagali-primary'
                      }`}
                      placeholder="Repita a password"
                    />
                    {confirmPassword && confirmPassword !== formData.password && (
                      <p className="text-sm text-red-500 mt-1">As passwords nao coincidem</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Additional Info */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações Adicionais</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Morada
                  </label>
                  <input
                    type="text"
                    value={formData.morada || ''}
                    onChange={(e) => setFormData({ ...formData, morada: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    placeholder="Endereco completo"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={formData.cidade || ''}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                      placeholder="Ex: Praia, Mindelo..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website || ''}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                      placeholder="https://www.empresa.cv"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descricao do Negocio
                  </label>
                  <textarea
                    value={formData.descricao || ''}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    placeholder="Descreva o seu negocio, produtos ou servicos..."
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Resumo do Registo</h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Tipo:</dt>
                      <dd className="font-medium">{BUSINESS_TYPES.find(t => t.value === formData.tipoPerfil)?.label}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Negocio:</dt>
                      <dd className="font-medium">{formData.nomeNegocio}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Email:</dt>
                      <dd className="font-medium">{formData.email}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Username:</dt>
                      <dd className="font-medium">{formData.username}</dd>
                    </div>
                  </dl>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="terms"
                    required
                    className="mt-1 h-4 w-4 text-pagali-primary focus:ring-pagali-primary border-gray-300 rounded"
                  />
                  <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                    Li e aceito os{' '}
                    <a href="/terms" target="_blank" className="text-pagali-primary hover:underline">
                      Termos e Condicoes
                    </a>
                    {' '}e a{' '}
                    <a href="/privacy" target="_blank" className="text-pagali-primary hover:underline">
                      Politica de Privacidade
                    </a>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Voltar
                </button>
              ) : (
                <Link
                  to="/login"
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Ja tenho conta
                </Link>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={step === 1 ? !validateStep1() : !validateStep2()}
                  className="px-6 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continuar
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {loading ? 'A submeter...' : 'Submeter Registo'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BusinessRegister;
